import { getFontSize } from '@tamagui/font-size';
import { getTokenRelative } from '@tamagui/get-token';
import { Eye, EyeOff } from '@tamagui/lucide-icons';
import { Pressable } from '@unistocks/components/basic/pressable';
import { PropsWithRef } from '@unistocks/types';
import React, { useState } from 'react';
import type { ColorTokens, FontSizeTokens, GetProps, GetRef, Variable } from 'tamagui';
import {
  Paragraph,
  Input as TMInput,
  View,
  XGroup,
  createStyledContext,
  getFontSizeVariable,
  isWeb,
  styled,
  useCurrentColor,
  useGetThemedIcon,
  withStaticProperties
} from 'tamagui';

const INPUT_NAME = 'Input';

const InputContext = createStyledContext<{
  size: FontSizeTokens;
  scaleIcon: number;
  color?: ColorTokens;
  disabled?: boolean;
  invalid?: boolean;
}>({
  size: '$5',
  scaleIcon: 1.4,
  color: '$bodyFont',
  disabled: false,
  invalid: false
});

const FocusContext = createStyledContext({
  setFocused: (_val: boolean) => {},
  focused: false
});

const InputGroupFrame = styled(XGroup, {
  name: INPUT_NAME,
  context: InputContext,
  justifyContent: 'space-between',

  variants: {
    unstyled: {
      false: {
        backgroundColor: '$color1',
        borderColor: '$color5',
        borderWidth: 1,
        outlineWidth: 0,
        // this fixes a flex bug where it overflows container
        minWidth: 0,

        hoverStyle: {
          borderColor: '$borderColorHover'
        },

        focusStyle: {
          outlineColor: '$outlineColor',
          outlineWidth: 2,
          outlineStyle: 'solid',
          borderColor: isWeb ? '$color5' : '$color6',
          borderWidth: isWeb ? 1 : '$1'
        },

        focusVisibleStyle: {
          outlineStyle: 'solid',
          outlineWidth: 2,
          outlineColor: '$outlineColor'
        }
      }
    },
    scaleIcon: {
      ':number': {} as any
    },
    /* applyFocusStyle: {
      ':boolean': (val, { props }) => {
        if (val && !(props as any).invalid) {
          return props.focusStyle || defaultInputGroupStyles.focusStyle;
        }
      }
    }, */
    size: {
      '...size': (val, { tokens }) => {
        return {
          borderRadius: tokens.radius[val as keyof (typeof tokens)['radius']]
        };
      }
    },
    invalid: {
      true: {
        outlineColor: '$danger',
        outlineWidth: 1,
        outlineStyle: 'solid',
        borderColor: '$danger',
        borderWidth: isWeb ? 1 : '$1',

        hoverStyle: {
          borderColor: '$danger'
        },

        focusStyle: {
          outlineColor: '$danger',
          outlineWidth: 2,
          outlineStyle: 'solid',
          borderColor: '$danger',
          borderWidth: isWeb ? 1 : '$1'
        }
      }
    },
    disabled: {
      true: {
        cursor: 'default',
        pointerEvents: 'none',
        backgroundColor: '$gray3',
        borderColor: '$gray5'
      }
    }
  } as const,

  defaultVariants: {
    unstyled: false
  }
});

const InputGroupImpl = InputGroupFrame.styleable(({ children, ...rest }, forwardedRef) => {
  const [focused, setFocused] = useState(false);

  return (
    <FocusContext.Provider focused={focused} setFocused={setFocused}>
      <InputGroupFrame ref={forwardedRef} forceStyle={focused ? 'focus' : undefined} {...rest}>
        {children}
      </InputGroupFrame>
    </FocusContext.Provider>
  );
});

/**
 * Input Section, wrapping the different parts of the input (icon, input, button)
 */
const InputSection = styled(XGroup.Item, {
  context: InputContext,
  justifyContent: 'center',
  alignItems: 'center'
});

//#region Input
const InputArea = styled(TMInput, {
  context: InputContext,
  backgroundColor: 'transparent',
  borderWidth: 0,

  focusStyle: {
    borderWidth: 0
  },

  focusVisibleStyle: {
    outlineWidth: 0
  },

  variants: {
    disabled: {
      true: {
        readOnly: true,
        disabled: true
      }
    }
  }
});

const InputImpl = InputArea.styleable(({ onFocus, onBlur, ...rest }, ref) => {
  const { setFocused } = FocusContext.useStyledContext();
  const { size, color } = InputContext.useStyledContext();

  return (
    <View flex={1}>
      <InputArea
        ref={ref}
        size={size}
        color={color}
        cursorColor={color}
        {...rest}
        onFocus={e => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={e => {
          setFocused(false);
          onBlur?.(e);
        }}
      />
    </View>
  );
});
//#endregion

//#region Input Append/Prepend
const InputIconFrame = styled(View, {
  context: InputContext,
  justifyContent: 'center',
  alignItems: 'center',

  variants: {
    size: {
      '...size': (val, { tokens }) => {
        return {
          paddingHorizontal: tokens.space[val as keyof (typeof tokens)['space']]
        };
      }
    }
  } as const
});

const getIconSize = (size: FontSizeTokens, scale: number) => {
  return (typeof size === 'number' ? size * 0.5 : getFontSize(size)) * scale;
};

const InputIcon = InputIconFrame.styleable<{
  scaleIcon?: number;
  color?: ColorTokens | string;
}>((props, ref) => {
  const { children, color: colorProp, ...rest } = props;
  const inputContext = InputContext.useStyledContext();
  const { size: contextSize = '$true', color: contextColor, scaleIcon = 1 } = inputContext;

  const color = useCurrentColor(contextColor || '$color10');
  const size = getIconSize(contextSize as FontSizeTokens, scaleIcon);

  const getThemedIcon = useGetThemedIcon({ size, color });

  return (
    <InputIconFrame ref={ref} {...rest}>
      {getThemedIcon(children)}
    </InputIconFrame>
  );
});

const AppendTextFrame = styled(View, {
  context: InputContext,
  justifyContent: 'center',
  alignItems: 'center',
  px: '$4'
});

const InputAppendText = AppendTextFrame.styleable(({ children, ...props }, ref) => {
  const { size } = InputContext.useStyledContext();
  const downShifted = getTokenRelative('size', size, { shift: -1, excludeHalfSteps: true });
  const fontSize = getFontSizeVariable(
    downShifted.key as FontSizeTokens
  ) as unknown as Variable<number>;

  return (
    <AppendTextFrame ref={ref} {...props}>
      <Paragraph fontSize={fontSize.val} color={'$bodyFont'} fontWeight={'$7'}>
        {children}
      </Paragraph>
    </AppendTextFrame>
  );
});
//#endregion

const InputFrame = styled(View, {
  context: InputContext,
  flexDirection: 'column',

  variants: {
    size: {
      '...size': (val, { tokens }) => ({
        gap: tokens.space[val as keyof (typeof tokens)['space']].val * 0.3
      })
    },
    color: {
      '...color': () => ({})
    },
    invalid: { ':boolean': () => ({}) },
    disabled: { ':boolean': () => ({}) }
  } as const,

  defaultVariants: {
    size: '$5'
  }
});

export type InputFrameProps = GetProps<typeof Input>;
export type InputProps = PropsWithRef<GetProps<typeof InputImpl>, typeof InputArea>;
export type InputRef = GetRef<typeof InputArea>;

export const Input = withStaticProperties(InputFrame, {
  Box: InputGroupImpl,
  Area: InputImpl,
  Section: InputSection,
  Icon: InputIcon,
  AppendText: InputAppendText
});

export type ShowPasswordInputIconProps = {
  showPassword: boolean;
  setShowPassword: (showPassword: boolean) => void;
};

export const ShowPasswordInputIcon: React.FC<ShowPasswordInputIconProps> = ({
  showPassword,
  setShowPassword
}) => (
  <Pressable onPressIn={() => setShowPassword(true)} onPressOut={() => setShowPassword(false)}>
    {showPassword ? <Eye /> : <EyeOff />}
  </Pressable>
);

ShowPasswordInputIcon.displayName = 'ShowPasswordInputIcon';
